import { AspectRatio, Box, Center, Text } from "@repo/ui/core";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { useEmulatorUIOptional } from "../../emulator/EmulatorUIContext";
import { useCuttlefishHealth } from "../hooks/useCuttlefishHealth";
import { useCuttlefishConnection } from "../providers/CuttlefishProvider";
import {
  DisplayErrorOverlay,
  DISPLAY_STREAM_TIMEOUT_MS,
} from "./DisplayErrorOverlay";

export interface CuttlefishDisplayProps {
  displayId: number;
  rotation?: number;
  onLoaded?: () => void;
  style?: CSSProperties;
}

export interface CuttlefishDisplayRef {
  displayId: number;
  element: HTMLVideoElement;
}

export const CuttlefishDisplay = forwardRef<
  CuttlefishDisplayRef,
  CuttlefishDisplayProps
>(({ displayId, rotation = 0, onLoaded, style }, ref) => {
  const connection = useCuttlefishConnection();
  const health = useCuttlefishHealth(connection);
  const emulatorUI = useEmulatorUIOptional();
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);

  // Forward the internal video ref to the parent
  useImperativeHandle(
    ref,
    () => ({
      displayId,
      element: videoRef!,
    }),
    [videoRef, displayId],
  );
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [streamTimedOut, setStreamTimedOut] = useState(false);

  const streamId = `display_${displayId}`;
  const deviceNotPublishingVideo = !!health?.deviceNotPublishingVideo;

  useEffect(() => {
    const currentStream = connection?.getStream(streamId);
    if (
      currentStream &&
      currentStream.active &&
      currentStream.getVideoTracks().length > 0
    ) {
      setStream(currentStream);
    } else {
      setStream(null);
    }

    const handleStreamChange = (changedStream: MediaStream) => {
      if (changedStream.id !== streamId) return;
      // removetrack: when the last video track is pulled the display must
      // return to the waiting state rather than show a frozen/black frame.
      const hasVideo =
        changedStream.active && changedStream.getVideoTracks().length > 0;
      setStream(hasVideo ? changedStream : null);
      if (hasVideo) setStreamTimedOut(false);
    };

    connection?.onStreamChange(handleStreamChange);

    return () => {
      // Clear the video element source
      if (videoRef) {
        videoRef.srcObject = null;
      }
      // Unregister the specific stream change handler
      connection?.offStreamChange(handleStreamChange);
    };
  }, [connection, streamId, videoRef]);

  // 30s stream-arrival timeout (D5): only when the offer had video but the
  // track never arrived. deviceNotPublishingVideo short-circuits to its own
  // hard error immediately (D4), so the timeout is irrelevant there.
  useEffect(() => {
    setStreamTimedOut(false);
    if (stream || deviceNotPublishingVideo || !connection) return;
    const timer = setTimeout(
      () => setStreamTimedOut(true),
      DISPLAY_STREAM_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [stream, deviceNotPublishingVideo, connection, streamId]);

  useEffect(() => {
    if (videoRef && stream) {
      videoRef.srcObject = stream;
    }
  }, [videoRef, stream]);

  const aspectRatio = useMemo<number>(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return 16 / 9;
    return dimensions.width / dimensions.height;
  }, [dimensions]);

  const handleLoadedMetadata = () => {
    if (videoRef) {
      setDimensions({
        width: videoRef.videoWidth,
        height: videoRef.videoHeight,
      });
      onLoaded?.();
    }
  };

  const getTransform = () => {
    if (rotation === 0) return "none";

    const aspectRatio = dimensions.width / dimensions.height;
    const r = (Math.PI * rotation) / 180;

    const tmp1 = Math.max(
      Math.abs(Math.cos(r) + Math.sin(r) / aspectRatio),
      Math.abs(Math.cos(r) - Math.sin(r) / aspectRatio),
    );
    const leftFactor = (tmp1 - 1) / 2;

    const tmp2 = Math.max(
      Math.abs(Math.cos(r) - Math.sin(r) * aspectRatio),
      Math.abs(Math.cos(r) + Math.sin(r) * aspectRatio),
    );
    const topFactor = (tmp2 - 1) / 2;

    return `translate(calc(100% * ${leftFactor}), calc(100% * ${topFactor})) rotate(${-rotation}deg)`;
  };

  const restartProps = {
    onRestart: emulatorUI?.onRestartClick,
    isRestarting: emulatorUI?.isRestarting,
    disableRestart: emulatorUI?.disableRestart,
  };

  // Hard error: device offer carried no video m-line (D4)
  if (deviceNotPublishingVideo) {
    return (
      <Box style={{ position: "relative", ...style }}>
        <AspectRatio ratio={aspectRatio}>
          <Box style={{ position: "relative", width: "100%", height: "100%" }}>
            <DisplayErrorOverlay
              title="Emulator isn't publishing video"
              message="The device is connected but its graphics pipeline is down, so no video is being sent. Restart the emulator to restore the display."
              {...restartProps}
            />
          </Box>
        </AspectRatio>
      </Box>
    );
  }

  if (!stream) {
    // Hard error: offer had video but the display stream never arrived (D5)
    if (streamTimedOut) {
      return (
        <Box style={{ position: "relative", ...style }}>
          <AspectRatio ratio={aspectRatio}>
            <Box
              style={{ position: "relative", width: "100%", height: "100%" }}
            >
              <DisplayErrorOverlay
                title={`Display ${displayId} stream timed out`}
                message="The emulator negotiated video but the display stream never arrived within 30s. Restart the emulator to try again."
                {...restartProps}
              />
            </Box>
          </AspectRatio>
        </Box>
      );
    }

    return (
      <Box p="xl">
        <Text c="dimmed" ta="center">
          Waiting for display stream...
        </Text>
      </Box>
    );
  }

  return (
    <Box style={{ position: "relative", ...style }}>
      <AspectRatio ratio={aspectRatio}>
        <video
          ref={setVideoRef}
          id={`display_${displayId}`}
          autoPlay
          muted
          onLoadedMetadata={handleLoadedMetadata}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transform: getTransform(),
          }}
        />
      </AspectRatio>
      <Center>
        <Text size="sm" c="dimmed" mb="xs">
          Display {displayId}
          {dimensions.width > 0 && ` ${dimensions.width}x${dimensions.height}`}
          {rotation !== 0 && ` (Rotated ${rotation}deg)`}
        </Text>
      </Center>
    </Box>
  );
});

CuttlefishDisplay.displayName = "CuttlefishDisplay";
